import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import totalRevenue from "./tot-revnue.svg";
import totalProduct from "./tot-products.svg";
import dashboardService from "../../../services/dashboardService";
import { toast } from "react-toastify";

const timePeriodsData = {
  Today: {
    revenue: 5570,
    customers: 1200,
    products: 120,
    salesData: [
      { name: "9:00", value: 30 },
      { name: "12:00", value: 45 },
      { name: "15:00", value: 35 },
      { name: "18:00", value: 50 },
      { name: "21:00", value: 40 },
    ],
    categoryData: [
      { name: "Men", value: 30 },
      { name: "Women", value: 45 },
      { name: "T-shirts", value: 15 },
      { name: "Others", value: 10 },
    ],
    orders: [
      {
        id: "#23442",
        item: "Art Silk Saree...",
        time: "12:00am",
        price: "$24",
      },
      { id: "#4546", item: "Art Silk Saree...", time: "1:00pm", price: "$52" },
      { id: "#15152", item: "Art Silk Saree...", time: "1:10pm", price: "$32" },
    ],
  },
  "One Week": {
    revenue: 38500,
    customers: 8500,
    products: 145,
    salesData: [
      { name: "Mon", value: 320 },
      { name: "Tue", value: 380 },
      { name: "Wed", value: 420 },
      { name: "Thu", value: 380 },
      { name: "Fri", value: 450 },
      { name: "Sat", value: 520 },
      { name: "Sun", value: 480 },
    ],
    categoryData: [
      { name: "Men", value: 35 },
      { name: "Women", value: 40 },
      { name: "T-shirts", value: 20 },
      { name: "Others", value: 5 },
    ],
    orders: [
      {
        id: "#23442",
        item: "Art Silk Saree...",
        time: "Mon 12:00",
        price: "$124",
      },
      {
        id: "#4546",
        item: "Art Silk Saree...",
        time: "Tue 13:00",
        price: "$152",
      },
      {
        id: "#15152",
        item: "Art Silk Saree...",
        time: "Wed 11:10",
        price: "$232",
      },
    ],
  },
  "One Month": {
    revenue: 156000,
    customers: 35000,
    products: 180,
    salesData: [
      { name: "Week 1", value: 3800 },
      { name: "Week 2", value: 4200 },
      { name: "Week 3", value: 3900 },
      { name: "Week 4", value: 4500 },
    ],
    categoryData: [
      { name: "Men", value: 40 },
      { name: "Women", value: 35 },
      { name: "T-shirts", value: 15 },
      { name: "Others", value: 10 },
    ],
    orders: [
      {
        id: "#23442",
        item: "Art Silk Saree...",
        time: "Week 1",
        price: "$524",
      },
      { id: "#4546", item: "Art Silk Saree...", time: "Week 2", price: "$652" },
      {
        id: "#15152",
        item: "Art Silk Saree...",
        time: "Week 3",
        price: "$432",
      },
    ],
  },
  "One Year": {
    revenue: 1850000,
    customers: 425000,
    products: 250,
    salesData: [
      { name: "Jan", value: 42000 },
      { name: "Mar", value: 38000 },
      { name: "May", value: 45000 },
      { name: "Jul", value: 48000 },
      { name: "Sep", value: 52000 },
      { name: "Nov", value: 58000 },
    ],
    categoryData: [
      { name: "Men", value: 38 },
      { name: "Women", value: 42 },
      { name: "T-shirts", value: 12 },
      { name: "Others", value: 8 },
    ],
    orders: [
      { id: "#23442", item: "Art Silk Saree...", time: "Jan", price: "$2424" },
      { id: "#4546", item: "Art Silk Saree...", time: "Feb", price: "$1852" },
      { id: "#15152", item: "Art Silk Saree...", time: "Mar", price: "$2132" },
    ],
  },
};

const COLORS = ["#8b5cf6", "#ec4899", "#f43f5e", "#a855f7"];

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center h-full text-gray-500 py-8">
    <svg
      className="w-16 h-16 mb-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
    <p className="text-lg font-medium">{message}</p>
  </div>
);

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("Today");
  const [filteredData, setFilteredData] = useState({
    analytics: [],
    revenue: 0,
    orders: 0,
  });
  const [dashboardData, setDashboardData] = useState({
    stats: {
      products: 0,
      orders: 0,
      revenue: 0,
      productGrowth: 0,
    },
    recentOrders: [],
    topProduct: null,
    inventoryAlerts: [],
    salesAnalytics: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [stats, orders, products, inventory, analytics] = await Promise.all(
        [
          dashboardService.getDashboardStats(),
          dashboardService.getRecentOrders(),
          dashboardService.getTopProducts(1),
          dashboardService.getInventoryAlerts(),
          dashboardService.getSalesAnalytics(),
        ]
      );

      const dashData = {
        stats,
        recentOrders: orders,
        topProduct: products[0],
        inventoryAlerts: inventory,
        salesAnalytics: analytics,
      };

      setDashboardData(dashData);
      filterDataByPeriod("Today", dashData);
    } catch (error) {
      toast.error(error.message || "Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    switch (selectedPeriod) {
      case "Today":
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      case "This Week":
        return d.toLocaleDateString([], { weekday: "short" });
      case "This Month":
        return d.toLocaleDateString([], { day: "numeric", month: "short" });
      default:
        return d.toLocaleDateString([], { day: "numeric", month: "short" });
    }
  };

  const filterDataByPeriod = (period, data = dashboardData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let startDate;
    let interval;

    switch (period) {
      case "Today":
        startDate = today;
        interval = "hour";
        break;

      case "This Week":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        interval = "day";
        break;

      case "This Month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        interval = "day";
        break;

      case "All Time":
      default:
        startDate = new Date(0);
        interval = "month";
        break;
    }

    // Filter orders and calculate revenue
    const filteredOrders = data.recentOrders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= startDate;
    });

    // Group sales data by interval
    const salesByInterval = filteredOrders.reduce((acc, order) => {
      const orderDate = new Date(order.createdAt);
      let key;

      switch (interval) {
        case "hour":
          key = new Date(
            orderDate.getFullYear(),
            orderDate.getMonth(),
            orderDate.getDate(),
            orderDate.getHours()
          ).toISOString();
          break;
        case "day":
          key = new Date(
            orderDate.getFullYear(),
            orderDate.getMonth(),
            orderDate.getDate()
          ).toISOString();
          break;
        case "month":
          key = new Date(
            orderDate.getFullYear(),
            orderDate.getMonth(),
            1
          ).toISOString();
          break;
      }

      if (!acc[key]) {
        acc[key] = {
          date: key,
          value: 0,
        };
      }
      acc[key].value += order.totalPrice;
      return acc;
    }, {});

    // Convert to array and sort by date
    const analytics = Object.values(salesByInterval).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    setFilteredData({
      analytics,
      revenue: filteredOrders.reduce((sum, order) => sum + order.totalPrice, 0),
      orders: filteredOrders.length,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">Loading...</div>
    );
  }

  return (
    <div className="p-8 min-h-screen pl-0 pr-0">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Dashboard</h1>

      {/* Time Filter */}
      <div className="flex gap-3 mb-6">
        {["Today", "This Week", "This Month", "All Time"].map((period) => (
          <button
            key={period}
            onClick={() => {
              setSelectedPeriod(period);
              filterDataByPeriod(period);
            }}
            className={`px-6 py-2 rounded-full ${
              selectedPeriod === period
                ? "bg-purple-600 text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            } shadow-sm transition-colors`}
          >
            {period}
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Revenue Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <p className="text-gray-400 mb-1">
            {selectedPeriod === "All Time"
              ? "Total Revenue"
              : `${selectedPeriod}'s Revenue`}
          </p>
          <div className="flex justify-between items-center">
            <div>
              {filteredData.revenue > 0 ? (
                <>
                  <h2 className="text-3xl font-bold">
                    ${filteredData.revenue.toLocaleString()}
                  </h2>
                  <p className="text-xs text-gray-400">
                    For {selectedPeriod.toLowerCase()}
                  </p>
                </>
              ) : (
                <p className="text-lg text-gray-500">No revenue yet</p>
              )}
            </div>
            <div className="w-16 h-16">
              <img
                src={totalRevenue}
                alt="revenue icon"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* Products Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <p className="text-gray-400 mb-1">Total Products</p>
          <div className="flex justify-between items-center">
            <div>
              {dashboardData.stats.products > 0 ? (
                <>
                  <h2 className="text-3xl font-bold">
                    {dashboardData.stats.products.toLocaleString()}
                  </h2>
                  <p className="text-xs text-gray-400">All active products</p>
                </>
              ) : (
                <p className="text-lg text-gray-500">No products added</p>
              )}
            </div>
            <div className="w-16 h-16">
              <img
                src={totalProduct}
                alt="products icon"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* Orders Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <p className="text-gray-400 mb-1">
            {selectedPeriod === "All Time"
              ? "Total Orders"
              : `${selectedPeriod}'s Orders`}
          </p>
          <div className="flex justify-between items-center">
            <div>
              {filteredData.orders > 0 ? (
                <>
                  <h2 className="text-3xl font-bold">
                    {filteredData.orders.toLocaleString()}
                  </h2>
                  <p className="text-xs text-gray-400">
                    For {selectedPeriod.toLowerCase()}
                  </p>
                </>
              ) : (
                <p className="text-lg text-gray-500">No orders yet</p>
              )}
            </div>
            <div className="w-16 h-16 flex items-center justify-center">
              <span className="text-4xl">📦</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-5 gap-6 mb-6">
        {/* Sales Analytics Chart */}
        <div className="col-span-3 bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-gray-600 font-medium">
              Sales Rate ({selectedPeriod})
            </h3>
            <div className="text-sm text-gray-500">
              Total Sales: ${filteredData.revenue.toLocaleString()}
            </div>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            {filteredData.analytics.length > 0 ? (
              <ResponsiveContainer>
                <LineChart
                  data={filteredData.analytics}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    stroke="#94a3b8"
                    tick={{ fill: "#64748b" }}
                    tickLine={{ stroke: "#94a3b8" }}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    tick={{ fill: "#64748b" }}
                    tickLine={{ stroke: "#94a3b8" }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                    labelFormatter={(date) => `Date: ${formatDate(date)}`}
                    formatter={(value) => [
                      `$${value.toLocaleString()}`,
                      "Sales",
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={{
                      fill: "#8b5cf6",
                      stroke: "#fff",
                      strokeWidth: 2,
                      r: 4,
                    }}
                    activeDot={{
                      fill: "#8b5cf6",
                      stroke: "#fff",
                      strokeWidth: 2,
                      r: 6,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState
                message={`No sales data available for ${selectedPeriod.toLowerCase()}`}
              />
            )}
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="col-span-2 bg-white rounded-2xl p-6 shadow-sm">
          <h3 className="text-gray-600 font-medium mb-4">Low Stock Alerts</h3>
          <div className="space-y-4 max-h-[300px] overflow-y-auto">
            {dashboardData.inventoryAlerts.length > 0 ? (
              dashboardData.inventoryAlerts.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-500">
                        Stock: {item.currentStock}
                      </span>
                      <span className="text-sm text-red-500">
                        (Below {item.threshold})
                      </span>
                    </div>
                  </div>
                  <button className="px-3 py-1 text-sm text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100">
                    Restock
                  </button>
                </div>
              ))
            ) : (
              <EmptyState message="No low stock alerts" />
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders Table */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="text-gray-600 font-medium mb-4">Recent Orders</h3>
        {dashboardData.recentOrders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-3 font-medium">Order ID</th>
                  <th className="pb-3 font-medium">Customer</th>
                  <th className="pb-3 font-medium">Product</th>
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dashboardData.recentOrders.map((order) => (
                  <tr key={order._id} className="text-gray-700">
                    <td className="py-3">#{order._id.slice(-6)}</td>
                    <td className="py-3">{order.customerName}</td>
                    <td className="py-3">{order.itemName}</td>
                    <td className="py-3">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      ${order.totalPrice.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="No orders available" />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
